import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import InfoPopup from "../../../../common/components/info-popup/info-popup";
import { adminChangePassword } from "../../../../api/admin/admin-profile.api";

interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose, onSuccess }) => {
    const [passwords, setPasswords] = useState({
        current: "",
        new: "",
        confirm: "",
    });
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [infoPopupMessage, setInfoPopupMessage] = useState("");
    const [errors, setErrors] = useState({
        current: "",
        new: "",
        confirm: "",
    });

    const validateField = (name: string, value: string) => {
        let error = "";
        if (name === "current") {
            if (!value) error = "Current password is required";
        } else if (name === "new") {
            if (!value) {
                error = "New password is required";
            } else if (value.length < 8) {
                error = "Password must be at least 8 characters";
            } else {
                const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
                if (!passwordRegex.test(value)) {
                    error = "Password must contain uppercase, lowercase, number and special character";
                }
            }
        } else if (name === "confirm") {
            if (!value) {
                error = "Confirm password is required";
            } else if (value !== passwords.new) {
                error = "Passwords do not match";
            }
        }
        setErrors(prev => ({ ...prev, [name]: error }));
        return error;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const currentError = validateField("current", passwords.current);
        const newError = validateField("new", passwords.new);
        const confirmError = validateField("confirm", passwords.confirm);

        if (currentError || newError || confirmError) return;

        try {
            setIsLoading(true);
            const formData = new URLSearchParams();
            formData.append("currentPassword", passwords.current);
            formData.append("newPassword", passwords.new);
            formData.append("confirmPassword", passwords.confirm);

            await adminChangePassword(formData);
            onSuccess();
            setPasswords({ current: "", new: "", confirm: "" });
            setErrors({ current: "", new: "", confirm: "" });
        } catch (error: any) {
            console.error("Error changing password:", error);
            const errorData = error?.response?.data;

            if (errorData?.code === "VALIDATION_ERROR" && Array.isArray(errorData.errors)) {
                const apiErrors = { ...errors };
                errorData.errors.forEach((errObj: any) => {
                    if (errObj.currentPassword || errObj.oldPassword) {
                        apiErrors.current = errObj.currentPassword || errObj.oldPassword;
                    }
                    if (errObj.newPassword) {
                        apiErrors.new = errObj.newPassword;
                    }
                    if (errObj.confirmPassword) {
                        apiErrors.confirm = errObj.confirmPassword;
                    }
                });
                setErrors(apiErrors);
                setInfoPopupMessage(errorData.message || "Input validation failed");
                setShowInfoPopup(true);
            } else {
                setInfoPopupMessage(errorData?.message || "Failed to change password. Please try again.");
                setShowInfoPopup(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth className="change-password-modal-dialog">
                <DialogTitle>
                    <div className="modal-header">
                        <span>Change Password</span>
                        <IconButton onClick={onClose} size="small" className="close-btn">
                            <CloseIcon />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent>
                    <form onSubmit={handleSubmit} className="password-modal-content">
                        <div className="form-group">
                            <label>Current Password <span className="required">*</span></label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showCurrent ? "text" : "password"}
                                    placeholder="Enter current password"
                                    value={passwords.current}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setPasswords({ ...passwords, current: val });
                                        validateField("current", val);
                                    }}
                                    className={errors.current ? "error-input" : ""}
                                />
                                <span className="toggle-password" onClick={() => setShowCurrent(!showCurrent)}>
                                    {showCurrent ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                                </span>
                            </div>
                            {errors.current && <span className="error-message">{errors.current}</span>}
                        </div>

                        <div className="form-group">
                            <label>New Password <span className="required">*</span></label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showNew ? "text" : "password"}
                                    placeholder="Enter new password (min. 8 characters)"
                                    value={passwords.new}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setPasswords({ ...passwords, new: val });
                                        validateField("new", val);
                                        // Also clear confirm error if they match now
                                        if (passwords.confirm && val === passwords.confirm) {
                                            setErrors(prev => ({ ...prev, confirm: "" }));
                                        }
                                    }}
                                    className={errors.new ? "error-input" : ""}
                                />
                                <span className="toggle-password" onClick={() => setShowNew(!showNew)}>
                                    {showNew ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                                </span>
                            </div>
                            {errors.new && <span className="error-message">{errors.new}</span>}
                        </div>

                        <div className="form-group">
                            <label>Confirm New Password <span className="required">*</span></label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showConfirm ? "text" : "password"}
                                    placeholder="Re-enter new password"
                                    value={passwords.confirm}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setPasswords({ ...passwords, confirm: val });
                                        validateField("confirm", val);
                                    }}
                                    className={errors.confirm ? "error-input" : ""}
                                />
                                <span className="toggle-password" onClick={() => setShowConfirm(!showConfirm)}>
                                    {showConfirm ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                                </span>
                            </div>
                            {errors.confirm && <span className="error-message">{errors.confirm}</span>}
                        </div>

                        <div className="modal-actions">
                            <button type="button" className="cancel-butn" onClick={onClose} disabled={isLoading}>Cancel</button>
                            <button type="submit" className="change-butn" disabled={isLoading}>{isLoading ? "Changing..." : "Change Password"}</button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <InfoPopup
                open={showInfoPopup}
                note={infoPopupMessage}
                onClose={() => setShowInfoPopup(false)}
            />
        </>
    );
};

export default ChangePasswordModal;
